'use client';
import { useEffect, useRef } from 'react';
import { useOperationFailure, useToast } from './Toast';

/** Reports the failure of an operation a surface owns from INSIDE that surface (a dialog or drawer), so the card
 *  belongs to it: its actions work there and it ends with the surface. A hook called by the component that opens the
 *  surface sits above it and would bind the card to the page behind. The failure is classified by the shared rules
 *  (`useOperationFailure`): an unknown outcome is an unconfirmed write offering `reload`, an ordinary refusal one plain
 *  card with the curated `message`. Pass a new `failure` object to report again; `null` ends the card. Renders nothing. */
export function OperationFailureReport({ reportKey, failure, message, reload }: {
  reportKey: string;
  failure: { error: unknown } | null;
  message: string;
  /** The owner's authoritative re-read; the card's only recovery for a write that may have been applied. */
  reload: () => void | Promise<void>;
}) {
  const { dismissKey } = useToast();
  const report = useOperationFailure();
  const latest = useRef({ message, reload });
  latest.current = { message, reload };
  useEffect(() => {
    if (!failure) { dismissKey(reportKey); return; }
    report(reportKey, failure.error, { message: latest.current.message, reload: () => latest.current.reload() });
  }, [failure, reportKey, dismissKey, report]);
  return null;
}
